import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentStatus = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [bookingId, setBookingId] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setBookingId(data.booking_id);
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 mx-auto mb-6 text-[#C2410C] animate-spin" />
            <h2 className="font-display text-2xl font-bold mb-2">
              Verificando pago...
            </h2>
            <p className="text-[#57534E]">
              Por favor, espera mientras verificamos tu pago.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2 text-green-600">
              {t.booking.success}
            </h2>
            <p className="text-[#57534E] mb-4">
              {t.booking.successMessage}
            </p>
            {bookingId && (
              <p className="text-sm text-[#57534E] mb-6">
                Booking ID: <span className="font-mono font-semibold">{bookingId}</span>
              </p>
            )}
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
              data-testid="payment-success-home-btn"
            >
              Volver al inicio
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2 text-red-600">
              Error en el pago
            </h2>
            <p className="text-[#57534E] mb-6">
              No se pudo procesar tu pago. Por favor, inténtalo de nuevo.
            </p>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
              data-testid="payment-failed-home-btn"
            >
              Volver al inicio
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatus;
